/**
 * ClassificationProgressScreen — Animated loading screen displayed while a
 * classification request is in progress. Accepts a single typed config object
 * that determines the layout, steps, and insight message.
 *
 * Two classification types:
 * - SdsConfig:        barcode/ASIN flow — single product thumbnail
 * - AiResearchConfig: image capture flow — horizontal scrollable captured images
 */

import React, { useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import ProgressTracker, { ProgressTrackerStep } from "@amzn/meridian/progress-tracker";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { Button, Icon, Loader, Thumbnail } from "@amzn/meridian";
import { useNavigate } from "react-router-dom";
import { ClassificationType } from "src/types/classificationTypes";
import CapturedImagesRow from "./CapturedImagesRow";
import "./classification.scss";

/* ── Step definitions ── */

interface Step {
  title: string;
  subtitle: string;
}

const SDS_STEPS: Step[] = [
  { title: "Retrieving Product Details", subtitle: "Fetching catalog and metadata" },
  { title: "Analyzing Catalog Attributes", subtitle: "Evaluating composition signals" },
  { title: "Checking SDS Availability", subtitle: "Searching for Safety Data Sheet" },
  { title: "Extracting Attributes", subtitle: "Parsing regulatory data" },
  { title: "Classification Rules", subtitle: "Running AI rule engine" },
  { title: "Finalizing Recommendation", subtitle: "Selecting waste profile" },
];

const AI_RESEARCH_STEPS: Step[] = [
  { title: "Processing Captured Images", subtitle: "" },
  { title: "Extracting Visual Features", subtitle: "" },
  { title: "Matching External Signals", subtitle: "" },
  { title: "Inferring Ingredients", subtitle: "" },
  { title: "Applying Classification Rules", subtitle: "" },
  { title: "Finalizing Waste Profile", subtitle: "" },
];

/** Duration per step in ms — exported so callers can compute total animation time */
export const STEP_DURATION_MS = 1000;
export const SDS_TOTAL_DURATION_MS = SDS_STEPS.length * STEP_DURATION_MS;
export const AI_RESEARCH_TOTAL_DURATION_MS = AI_RESEARCH_STEPS.length * STEP_DURATION_MS;

/* ── Config types (discriminated union) ── */

export interface SdsConfig {
  type: ClassificationType.SDS;
  imageUrl?: string;
  productName: string;
  asin: string;
  aiInsightMessage: string;
}

export interface AiResearchConfig {
  type: ClassificationType.AI_RESEARCH;
  capturedImages: string[];
  aiInsightMessage: string;
}

export type ClassificationConfig = SdsConfig | AiResearchConfig;

/* ── Internal helpers ── */

enum StepStatus {
  PAST = "past",
  PRESENT = "present",
  FUTURE = "future",
}

const getStepType = (idx: number, completedCount: number): StepStatus => {
  if (idx < completedCount) return StepStatus.PAST;
  if (idx === completedCount) return StepStatus.PRESENT;
  return StepStatus.FUTURE;
};

/* ── Component ── */

interface Props {
  classificationConfig: ClassificationConfig;
  isReadyToNavigate?: boolean;
}

const ClassificationProgressScreen: React.FC<Props> = ({ classificationConfig, isReadyToNavigate = true }) => {
  const steps = classificationConfig.type === ClassificationType.SDS ? SDS_STEPS : AI_RESEARCH_STEPS;

  const [completedCount, setCompletedCount] = useState(0);
  const navigate = useNavigate();

  // Advance one step every STEP_DURATION_MS until all steps are done
  useEffect(() => {
    if (completedCount >= steps.length) return;
    const timer = setTimeout(() => setCompletedCount((prev) => prev + 1), STEP_DURATION_MS);
    return () => clearTimeout(timer);
  }, [completedCount, steps.length]);

  return (
    <Column className="app-gradient-bg home-content pwa-scrollable" spacingInset="500" spacing="400">

      {/* Header */}
      <Row alignmentVertical="center" spacingInset="300 none none" spacing="400">
        <Button type="icon" onClick={() => navigate("/home")}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Column spacing="200" spacingInset="400 none">
          <Text type="h400">Analyzing Product...</Text>
          <Text type="b200" color="secondary">
            Processing product data and determining compliant waste profile
          </Text>
        </Column>
      </Row>

      {/* Product card — differs by classification type */}
      {classificationConfig.type === ClassificationType.SDS ? (
        <Row className="home-card" spacing="400" spacingInset="400" alignmentVertical="center">
          {classificationConfig.imageUrl && (
            <Thumbnail source={classificationConfig.imageUrl} size="medium" type="square" />
          )}
          <Column spacing="100">
            <Text type="h100">{classificationConfig.productName}</Text>
            <Text type="b100" color="secondary">ASIN: {classificationConfig.asin}</Text>
          </Column>
        </Row>
      ) : (
        <CapturedImagesRow
          images={classificationConfig.capturedImages}
          badgeLabel="ANALYZING"
        />
      )}

      {/* Timeline steps */}
      <Column className="home-card" spacingInset="500">
        <ProgressTracker direction="column" type="theme" autoTrackLength>
          {steps.map((step, idx) => (
            <ProgressTrackerStep key={idx} type={getStepType(idx, completedCount)} label={step.title}>
              {step.subtitle && <Text type="b100" color="secondary">{step.subtitle}</Text>}
            </ProgressTrackerStep>
          ))}
        </ProgressTracker>
        
        {completedCount >= steps.length && !isReadyToNavigate && (
          <Row alignmentHorizontal="center" spacingInset="400 none none">
            <Loader size="medium" type="circular" />
            <Text type="b300" color="secondary">Finalizing results...</Text>
          </Row>
        )}
      </Column>

      {/* AI Insight card */}
      <Column className="home-card" spacingInset="400" spacing="200">
        <Row spacing="200">
          <Icon color="success" tokens={sparklesSmallTokens} />
          <Text type="h50" color="success">AI INSIGHT</Text>
        </Row>
        <Text type="b300">{classificationConfig.aiInsightMessage}</Text>
      </Column>
    </Column>
  );
};

export default ClassificationProgressScreen;
